<template>
  <span ref="root" class="identity-mark" :class="{ 'identity-mark-static': !animated || expanded, 'identity-mark-full': expanded }" :style="{ '--identity-canvas-width': artwork.width }" aria-hidden="true">
    <svg ref="art" class="identity-art" :viewBox="`0 0 ${artwork.width} 44`" fill-rule="evenodd" focusable="false">
      <path v-if="expanded" :d="artwork.fullName" />
      <template v-else>
      <path ref="signature" :d="artwork.monogram" />
      <g ref="initials" opacity="0">
        <path ref="initialH" :d="artwork.initials[0]" />
        <path ref="initialW" :d="artwork.initials[1]" />
      </g>
      <g ref="name" opacity="0">
        <path v-for="(suffix, index) in artwork.suffixes" :key="index" :d="suffix" />
      </g>
      <path ref="fullName" :d="artwork.fullName" opacity="0" />
      </template>
    </svg>
  </span>
</template>

<script setup>
import { defineProps, onBeforeUnmount, onMounted, ref } from 'vue';
import artwork from '../../assets/identity/identity-motion.json';

const props = defineProps({ animated: { type: Boolean, default: true }, expanded: { type: Boolean, default: false } });
const root = ref(null), art = ref(null), signature = ref(null), initials = ref(null);
const initialH = ref(null), initialW = ref(null), name = ref(null), fullName = ref(null);
let owner, motionQuery, hoverQuery, frame = 0, progress = 0, target = 0;
let hovered = false, keyboardFocused = false, previousTime = 0;
const smooth = t => t * t * (3 - 2 * t);
const clamp = t => Math.min(1, Math.max(0, t));
const morph = (contour, t) => contour.source.map((point, i) => {
  const destination = contour.target[i];
  return `${i ? 'L' : 'M'}${(point[0] + (destination[0] - point[0]) * t).toFixed(3)} ${(point[1] + (destination[1] - point[1]) * t).toFixed(3)}`;
}).join('') + 'Z';

function paint() {
  const t = smooth(progress);
  // Use the exact approved curves at both ends. Only the brief in-between
  // state uses matched contour points, so the resting logo is never faceted.
  signature.value.setAttribute('opacity', progress === 0 ? '1' : '0');
  initials.value.setAttribute('opacity', progress === 0 || progress === 1 ? '0' : '1');
  fullName.value.setAttribute('opacity', progress === 1 ? '1' : '0');
  [initialH.value, initialW.value].forEach((path, i) => {
    path.setAttribute('d', progress === 1 ? artwork.initials[i] : morph(artwork.contours[i], t));
  });
  const reveal = smooth(clamp((progress - .42) / .58));
  name.value.setAttribute('opacity', progress === 1 ? '0' : reveal.toFixed(3));
  name.value.setAttribute('transform', `translate(${(1 - reveal) * -8} 0)`);
  // The ink expands inside its own canvas. Surrounding navigation keeps its
  // geometry, including during a reversed hover.
  art.value.style.clipPath = `inset(0 ${(1 - t) * (1 - 77 / artwork.width) * 100}% 0 0)`;
}

function tick(time) {
  const step = Math.min(32, time - (previousTime || time)) / 380;
  previousTime = time;
  progress = target > progress ? Math.min(target, progress + step) : Math.max(target, progress - step);
  paint();
  if (progress !== target) frame = requestAnimationFrame(tick);
  else { frame = 0; previousTime = 0; }
}
function update() {
  target = props.animated && (keyboardFocused || (hovered && hoverQuery.matches)) ? 1 : 0;
  if (motionQuery.matches || keyboardFocused) {
    cancelAnimationFrame(frame); frame = 0; previousTime = 0;
    progress = target; paint();
  } else if (!frame && target !== progress) frame = requestAnimationFrame(tick);
}
const enter = event => { if (event.pointerType === 'mouse') { hovered = true; update(); } };
const leave = () => { hovered = false; update(); };
const focus = () => { keyboardFocused = owner.matches(':focus-visible'); update(); };
const blur = () => { keyboardFocused = false; update(); };

onMounted(() => {
  if (!props.animated || props.expanded) return;
  owner = root.value.closest('a, button') || root.value;
  motionQuery = matchMedia('(prefers-reduced-motion: reduce)');
  hoverQuery = matchMedia('(hover: hover) and (pointer: fine)');
  owner.addEventListener('pointerenter', enter);
  owner.addEventListener('pointerleave', leave);
  owner.addEventListener('focus', focus);
  owner.addEventListener('blur', blur);
  motionQuery.addEventListener('change', update);
  hoverQuery.addEventListener('change', update);
});
onBeforeUnmount(() => {
  cancelAnimationFrame(frame);
  owner?.removeEventListener('pointerenter', enter);
  owner?.removeEventListener('pointerleave', leave);
  owner?.removeEventListener('focus', focus);
  owner?.removeEventListener('blur', blur);
  motionQuery?.removeEventListener('change', update);
  hoverQuery?.removeEventListener('change', update);
});
</script>

<style scoped>
.identity-mark {
  --identity-height: 40px;
  position: relative;
  display: inline-block;
  width: calc(var(--identity-height) * 1.8);
  height: var(--identity-height);
  vertical-align: middle;
  user-select: none;
}
.identity-mark::before {
  content: '';
  position: absolute;
  inset: -6px auto -6px 0;
  width: 100%;
}
.identity-art {
  display: block;
  position: absolute;
  inset: 0 auto auto 0;
  width: calc(var(--identity-height) * var(--identity-canvas-width) / 44);
  height: 100%;
  max-width: none;
  fill: currentColor;
  pointer-events: none;
  overflow: visible;
  clip-path: inset(0 calc(100% - var(--identity-height) * 77 / 44) 0 0);
}
:global(a:focus-visible:has(> .identity-mark)),
:global(button:focus-visible:has(> .identity-mark:not(.identity-mark-static))) {
  outline: none;
}
:global(a:focus-visible > .identity-mark:not(.identity-mark-static)::after) {
  content: '';
  position: absolute;
  inset: -5px auto -5px -5px;
  width: calc(var(--identity-height) * var(--identity-canvas-width) / 44 + 10px);
  border: 1px solid currentColor;
  border-radius: 2px;
  pointer-events: none;
}
.identity-mark-static { --identity-height: 32px; }
.identity-mark-full { width: calc(var(--identity-height) * var(--identity-canvas-width) / 44); height: auto; aspect-ratio: var(--identity-canvas-width) / 44; }
.identity-mark-full .identity-art { position: static; width: 100%; height: 100%; clip-path: none; }
:global(a:focus-visible:has(> .identity-mark-full)) { outline: 1px solid currentColor; outline-offset: 4px; }
@media (hover: hover) and (pointer: fine) {
  .identity-mark:not(.identity-mark-static):hover::before {
    width: calc(var(--identity-height) * var(--identity-canvas-width) / 44);
  }
}
@media (max-width: 1100px) {
  .identity-mark { --identity-height: 32px; }
}
</style>
